/**
 * googleEarthWebUrl — deep-link helpers for Google Earth Web.
 *
 * Phase 1 integration: open the live site centroid in Earth Web with
 * sensible camera defaults. No API keys — Earth Web is a public URL.
 */
import type { SiteBoundaryAggregate, SiteBoundaryParcel } from '../types/siteBoundary';
import {
  computeAggregate,
  materializeSiteBoundaries,
  polygonCentroid,
} from './siteBoundaryGeometry';

export interface GoogleEarthWebViewOptions {
  altitudeM?: number;
  headingDeg?: number;
  tiltDeg?: number;
  rangeM?: number;
}

const DEFAULT_VIEW: Required<GoogleEarthWebViewOptions> = {
  altitudeM: 1500,
  headingDeg: 0,
  tiltDeg: 55,
  rangeM: 5000,
};

export function isValidLatLng(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !(lat === 0 && lng === 0)
  );
}

/** Round to six decimal places (~0.1 m) for stable share URLs. */
function fmtCoord(n: number): string {
  return Number(n.toFixed(6)).toString();
}

export function buildGoogleEarthWebUrl(
  lat: number,
  lng: number,
  options: GoogleEarthWebViewOptions = {},
): string {
  const { altitudeM, headingDeg, tiltDeg, rangeM } = { ...DEFAULT_VIEW, ...options };
  return `https://earth.google.com/web/@${fmtCoord(lat)},${fmtCoord(lng)},${altitudeM}a,${headingDeg}d,${tiltDeg}y,${rangeM}m`;
}

export function openGoogleEarthWeb(
  center: { lat: number; lng: number },
  options?: GoogleEarthWebViewOptions,
): void {
  window.open(buildGoogleEarthWebUrl(center.lat, center.lng, options), '_blank', 'noopener,noreferrer');
}

export type SiteEarthViewInput = {
  siteCentroid?: { lat: number; lng: number };
  siteBoundary?: Array<{ lat: number; lng: number }>;
  siteBoundaries?: SiteBoundaryParcel[];
  siteBoundaryAggregate?: SiteBoundaryAggregate;
  siteArea?: number;
  sitePerimeter?: number;
};

/**
 * Resolve the lat/lng to frame in Earth Web — prefers the persisted
 * multi-parcel aggregate centroid, then siteCentroid, then a boundary
 * vertex mean. Returns null when no usable site location exists.
 */
export function resolveSiteEarthViewCenter(site: SiteEarthViewInput): { lat: number; lng: number } | null {
  if (site.siteBoundaryAggregate?.aggregateCentroid) {
    const c = site.siteBoundaryAggregate.aggregateCentroid;
    if (isValidLatLng(c.lat, c.lng)) return c;
  }

  const parcels = materializeSiteBoundaries(site);
  if (parcels.length > 0) {
    const aggregate = site.siteBoundaryAggregate ?? computeAggregate(parcels);
    const c = aggregate.aggregateCentroid;
    if (isValidLatLng(c.lat, c.lng)) return c;
  }

  if (site.siteCentroid && isValidLatLng(site.siteCentroid.lat, site.siteCentroid.lng)) {
    return site.siteCentroid;
  }

  if (Array.isArray(site.siteBoundary) && site.siteBoundary.length >= 3) {
    const c = polygonCentroid(site.siteBoundary);
    if (isValidLatLng(c.lat, c.lng)) return c;
  }

  return null;
}

export function siteHasEarthViewLocation(site: SiteEarthViewInput): boolean {
  return resolveSiteEarthViewCenter(site) !== null;
}
